import React, { useState, useEffect, useCallback } from "react";
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle, 
  Search, 
  Check, 
  X, 
  Package, 
  User, 
  Calendar, 
  MessageSquare,
  Boxes,
  Filter,
  Eye,
  RotateCcw,
  ClipboardList
} from "lucide-react";
import { pb } from "../services/pocketbase";
import { loteService } from "../services/loteService";
import { useCatalogContext } from "../contexts/CatalogContext";
import { resolveCatalogSku } from "../utils/skuUtils";

export default function SolicitacoesSubTab({ user, inventory, findCatalogItem, onRefresh }) {
  const { catalog: catalogMap } = useCatalogContext();
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("pendentes"); // 'pendentes', 'aprovados', 'rejeitados'
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOrigem, setFilterOrigem] = useState("TODOS");

  // Modal de Rejeição de Lote
  const [rejectingLote, setRejectingLote] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [isRejecting, setIsRejecting] = useState(false);

  // Modal de Aprovação de Lote (loading state)
  const [approvingLoteId, setApprovingLoteId] = useState(null);

  // Carrega todos os lotes para unificação
  const loadAllLotes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await pb.collection("estoque_lotes").getFullList({
        sort: "-created",
        expand: "criado_por,aprovado_por"
      });
      setLotes(res);
    } catch (e) {
      console.error("Erro ao carregar solicitações de lotes:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllLotes();
  }, [loadAllLotes]);

  // Contadores por Status
  const countPendentes = lotes.filter(l => l.status === "aguardando_aprovacao" || l.status === "pendente").length;
  const countAprovados = lotes.filter(l => l.status === "em_producao" || l.status === "concluido").length;
  const countRejeitados = lotes.filter(l => l.status === "cancelado" || l.status === "rejeitado").length;

  // Filtragem de lotes por SubTab ativa
  const filteredLotes = lotes.filter(l => {
    const isPendente = l.status === "aguardando_aprovacao" || l.status === "pendente";
    const isAprovado = l.status === "em_producao" || l.status === "concluido";
    const isRejeitado = l.status === "cancelado" || l.status === "rejeitado";

    if (activeTab === "pendentes" && !isPendente) return false;
    if (activeTab === "aprovados" && !isAprovado) return false;
    if (activeTab === "rejeitados" && !isRejeitado) return false;

    const matchSearch = searchTerm === "" || 
      (l.codigo && l.codigo.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (l.solicitante && l.solicitante.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (l.observacoes && l.observacoes.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchOrigem = filterOrigem === "TODOS" || l.origem === filterOrigem;
    return matchSearch && matchOrigem;
  });

  // Ação: Aprovar Lote
  const handleApprove = async (loteId) => {
    if (!window.confirm("Deseja realmente aprovar este lote e iniciar a produção física? Os insumos BOM serão debitados.")) return;

    setApprovingLoteId(loteId);
    try {
      await loteService.approveLote(loteId, user);
      alert("Lote aprovado com sucesso! Produção iniciada.");
      loadAllLotes();
      if (onRefresh) onRefresh();
    } catch (e) {
      alert("Erro ao aprovar lote: " + e.message);
    } finally {
      setApprovingLoteId(null);
    }
  };

  // Ação: Confirmar Rejeição do Lote
  const handleConfirmReject = async () => {
    if (!rejectingLote || !rejectReason.trim() || isRejecting) return;

    setIsRejecting(true);
    try {
      await loteService.rejectLote(rejectingLote.id, user, rejectReason.trim());

      alert("Solicitação de lote rejeitada com sucesso.");
      setRejectingLote(null);
      setRejectReason("");
      loadAllLotes();
      if (onRefresh) onRefresh();
    } catch (e) {
      alert("Erro ao rejeitar lote: " + e.message);
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto animate-fade-in">
      
      {/* Header Unificado de Solicitações */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
            <ClipboardList className="text-purple-600" size={24} /> Central de Solicitações de Lotes
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Governança integrada de lotes manuais, solicitações da oficina e reposições geradas pelo planejamento.
          </p>
        </div>

        {/* Badges de Navegação por Estado */}
        <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200">
          <button
            onClick={() => setActiveTab("pendentes")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              activeTab === "pendentes" 
                ? "bg-amber-500 text-white shadow-sm font-extrabold" 
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <Clock size={14} /> Pendentes ({countPendentes})
          </button>

          <button
            onClick={() => setActiveTab("aprovados")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              activeTab === "aprovados" 
                ? "bg-emerald-600 text-white shadow-sm font-extrabold" 
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <CheckCircle size={14} /> Aprovados ({countAprovados})
          </button>

          <button
            onClick={() => setActiveTab("rejeitados")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              activeTab === "rejeitados" 
                ? "bg-red-600 text-white shadow-sm font-extrabold" 
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <XCircle size={14} /> Rejeitados ({countRejeitados})
          </button>
        </div>
      </div>

      {/* Barra de Busca e Filtros */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative flex-1 w-full">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Pesquisar por código do lote, solicitante ou observações..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:border-purple-500 text-slate-800"
          />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          <span className="text-xs text-slate-400 font-bold uppercase shrink-0">Origem:</span>
          <select
            value={filterOrigem}
            onChange={(e) => setFilterOrigem(e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:border-purple-500 text-slate-700"
          >
            <option value="TODOS">Todas as Origens</option>
            <option value="solicitacao_oficina">Solicitação da Oficina</option>
            <option value="planejamento">Planejamento de Demandas</option>
            <option value="escritorio">Manual / Escritório</option>
          </select>

          <button
            onClick={loadAllLotes}
            className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-all"
            title="Atualizar solicitações"
          >
            <RotateCcw size={16} />
          </button>
        </div>
      </div>

      {/* Lista de Cards de Solicitações */}
      {loading ? (
        <div className="text-center py-12 text-slate-400 text-xs italic">Carregando solicitações de lotes...</div>
      ) : filteredLotes.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl border border-slate-200 text-center space-y-3">
          <CheckCircle size={40} className="mx-auto text-slate-300" />
          <h3 className="text-sm font-bold text-slate-700">Nenhuma solicitação encontrada nesta aba.</h3>
          <p className="text-xs text-slate-400 max-w-sm mx-auto">
            {activeTab === "pendentes" && "Nenhum lote pendente de aprovação no momento."}
            {activeTab === "aprovados" && "Nenhum lote aprovado e em produção registrado."}
            {activeTab === "rejeitados" && "Nenhuma solicitação de lote rejeitada registrada."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLotes.map(lote => {
            const isApproving = approvingLoteId === lote.id;
            
            // 1. Resolve itens do inventário vinculados ao lote
            const loteInvItems = Array.isArray(inventory) ? inventory.filter(i => i.lote === lote.id) : [];

            // 2. Resolve itens declarados no lote (aceita tanto `items`, `itens_planejados` quanto `items_json`)
            let rawItems = lote.items || lote.itens_planejados || lote.items_json || [];
            if (typeof rawItems === "string") {
              try { rawItems = JSON.parse(rawItems); } catch(e) {}
            }

            let displayItems = Array.isArray(rawItems) ? rawItems : [];
            if (displayItems.length === 0 && loteInvItems.length > 0) {
              const skuCounts = {};
              loteInvItems.forEach(i => { skuCounts[i.sku] = (skuCounts[i.sku] || 0) + 1; });
              displayItems = Object.keys(skuCounts).map(sku => ({ sku, qtd: skuCounts[sku] }));
            }

            const totalPcs = displayItems.reduce((acc, curr) => acc + (Number(curr.qtd) || 1), 0);

            return (
              <div key={lote.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                
                {/* Detalhes Principais do Lote */}
                <div className="space-y-3 flex-1 min-w-0 w-full">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-black text-base text-slate-800">{lote.codigo}</span>
                    
                    <span className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full border ${
                      lote.status === "aguardando_aprovacao" || lote.status === "pendente"
                        ? "bg-amber-100 text-amber-800 border-amber-200"
                        : lote.status === "em_producao" || lote.status === "concluido"
                        ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                        : "bg-red-100 text-red-800 border-red-200"
                    }`}>
                      {lote.status}
                    </span>

                    <span className="text-xs font-bold text-slate-500 uppercase bg-slate-100 px-2 py-0.5 rounded">
                      Origem: {lote.origem || "Manual"}
                    </span>

                    {totalPcs > 0 && (
                      <span className="text-xs font-black text-purple-700 bg-purple-100 border border-purple-200 px-2.5 py-0.5 rounded-full">
                        Total: {totalPcs} pç{totalPcs !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>

                  {lote.observacoes && (
                    <p className="text-xs text-slate-600 italic bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                      Obs: {lote.observacoes}
                    </p>
                  )}

                  {/* ITENS DETALHADOS DO LOTE (S1.5) */}
                  {displayItems.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">Nenhum item discriminado no lote.</p>
                  ) : (
                    <div className="bg-slate-50/80 p-3 rounded-xl border border-slate-200 space-y-2">
                      <span className="text-[11px] font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                        <Package size={14} className="text-purple-600" />
                        Itens Solicitados para Produção ({totalPcs} un):
                      </span>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {displayItems.map((item, idx) => {
                          const cat = resolveCatalogSku(item.sku, catalogMap) || (findCatalogItem ? findCatalogItem(item.sku) : null);
                          return (
                            <div key={idx} className="bg-white p-2.5 rounded-lg border border-slate-200 flex items-center justify-between gap-2 shadow-2xs">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="font-extrabold text-xs text-purple-700">{item.sku}</span>
                                  <span className="bg-purple-100 text-purple-800 text-[10px] font-black px-1.5 py-0.2 rounded">
                                    x{item.qtd || 1}
                                  </span>
                                  {cat?.isInactive && (
                                    <span className="bg-slate-100 text-slate-500 text-[9px] font-bold px-1.5 py-0.2 rounded border border-slate-200">
                                      Inativo na loja
                                    </span>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-500 truncate mt-0.5 font-medium">
                                  {item.name || cat?.name || (cat?.sku ? `Produto Base: ${cat.sku}` : "Produto sem cadastro")}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Detalhamento Peça a Peça Físicas (S1.5) */}
                      {loteInvItems.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-slate-200">
                          <span className="text-[10px] font-black text-slate-600 uppercase tracking-wider block mb-1">
                            🔍 Peças Físicas Individuais no Lote ({loteInvItems.length} un):
                          </span>
                          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto custom-scrollbar p-1.5 bg-white rounded-lg border border-slate-200">
                            {loteInvItems.map(p => (
                              <div key={p.id} className="text-[9px] font-bold px-2 py-1 rounded bg-slate-50 border border-slate-200 text-slate-700 flex items-center gap-1.5 shadow-2xs">
                                <span className="font-mono text-purple-700">{p.sku}</span>
                                <span className="text-[8px] uppercase px-1 py-0.2 rounded bg-blue-100 text-blue-800 font-extrabold">{p.status || "Solicitado"}</span>
                                {p.situacao_fluxo === "suspenso" && <span className="text-[8px] bg-red-100 text-red-700 px-1 rounded font-black">SUSPENSA</span>}
                                {p.situacao_fluxo === "interceptado" && <span className="text-[8px] bg-amber-100 text-amber-700 px-1 rounded font-black">INTERCEPTADA</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-4 text-[10px] text-slate-400 font-medium pt-1">
                    <span className="flex items-center gap-1">
                      <User size={12} /> Solicitante: {lote.solicitante || "Sistema"}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={12} /> Data: {new Date(lote.created).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                </div>

                {/* Ações de Aprovação / Rejeição (Disponíveis em Pendentes) */}
                {(lote.status === "aguardando_aprovacao" || lote.status === "pendente") && (
                  <div className="flex flex-row md:flex-col gap-2 shrink-0 w-full md:w-auto justify-end border-t md:border-t-0 pt-3 md:pt-0 border-slate-100">
                    <button
                      onClick={() => handleApprove(lote.id)}
                      disabled={isApproving}
                      className="flex-1 md:flex-initial px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:bg-emerald-300"
                    >
                      <Check size={16} /> {isApproving ? "Aprovando..." : "Aprovar Lote"}
                    </button>

                    <button
                      onClick={() => {
                        setRejectingLote(lote);
                        setRejectReason("");
                      }}
                      disabled={isApproving}
                      className="flex-1 md:flex-initial px-4 py-2 bg-slate-100 hover:bg-red-50 text-slate-600 hover:text-red-700 border border-slate-200 hover:border-red-200 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <X size={16} /> Rejeitar
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL DE REJEIÇÃO OBRIGATÓRIA COM MOTIVO */}
      {rejectingLote && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-sm font-black text-slate-800 flex items-center gap-2">
                <AlertTriangle className="text-red-500" size={18} /> Rejeitar Solicitação de Lote
              </h3>
              <button onClick={() => setRejectingLote(null)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-500 uppercase block mb-1">
                Motivo da Rejeição (Obrigatório)
              </label>
              <textarea
                placeholder="Descreva o motivo claro da rejeição deste lote..."
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs outline-none focus:border-red-500 text-slate-800 h-24"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              ></textarea>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setRejectingLote(null)}
                className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmReject}
                disabled={!rejectReason.trim() || isRejecting}
                className="px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-sm disabled:bg-red-300"
              >
                Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
